import { createContext, type JSX, useContext, useMemo, useState } from 'react';
import type { Entitlement } from '@frontegg/nextjs';
import {
  UserEntitlementsContextType,
  UserEntitlementsProviderProps,
} from './userEntitlementsContext.types';
import { AuthenticatedEntitlementsUpdater } from './userEntitlementsAuthenticated';

export const UserEntitlementsContext =
  createContext<UserEntitlementsContextType | null>(null);

// Custom hook to use the context
export const useUserEntitlementsContext = (): UserEntitlementsContextType => {
  const context = useContext(UserEntitlementsContext);
  if (!context) {
    throw new Error(
      'useUserEntitlementsContext must be used within a UserEntitlementsProvider',
    );
  }
  return context;
};

// Patient
const usePatientEntitlementsContext = (): UserEntitlementsContextType => {
  const patientFeatureEntitlementOverride = (
    _featureKey: string,
  ): Entitlement => {
    // TODO: Should fetch room slug and call graphl operation to check if patient has access to feature - https://doxyme.atlassian.net/browse/PROD-2650
    throw new Error(
      'usePatientFeatureEntitlements is not implemented for unauthenticated users.',
    );
  };

  const patientPermissionEntitlementOverride = (
    _permissionKey: string,
  ): Entitlement => {
    // TODO: Should fetch room slug and call graphl operation to check if patient has access to permission - https://doxyme.atlassian.net/browse/PROD-2650
    throw new Error(
      'usePatientPermissionEntitlements is not implemented for unauthenticated users.',
    );
  };

  return useMemo(
    () => ({
      plan: undefined,
      isPaidUser: false,
      userPlanType: undefined,
      isLoading: false,
      isSuccess: true,
      isUpgradeButtonVisible: false,

      hasPaidSubscription: false,
      isTrustedCustomer: false,
      // Instead of throwing, we return a default entitlement object because we have some components in patient context that are not patient-specific
      useFeatureEntitlements: () => ({
        isEntitled: false,
        justification: undefined,
      }),
      usePermissionEntitlements: () => ({
        isEntitled: false,
        justification: undefined,
      }),
      usePatientFeatureEntitlements: patientFeatureEntitlementOverride,
      usePatientPermissionEntitlements: patientPermissionEntitlementOverride,
    }),
    [],
  );
};

// Main provider that conditionally renders based on authentication status
// ALWAYS use the same Provider component - never switches
export const UserEntitlementsProvider = ({
  children,
  isAuthenticated,
}: UserEntitlementsProviderProps): JSX.Element => {
  // Always call the patient context hook (doesn't use Frontegg)
  const patientContextValue = usePatientEntitlementsContext();

  // State to hold authenticated context value
  const [authenticatedContextValue, setAuthenticatedContextValue] =
    useState<UserEntitlementsContextType>(patientContextValue);

  const contextValue = isAuthenticated
    ? authenticatedContextValue
    : patientContextValue;

  return (
    <UserEntitlementsContext.Provider value={contextValue}>
      {/* Conditionally render updater, to not call frontegg hooks when not authenticated */}
      {isAuthenticated && (
        <AuthenticatedEntitlementsUpdater
          onUpdate={setAuthenticatedContextValue}
        />
      )}
      {children}
    </UserEntitlementsContext.Provider>
  );
};
